import React, { useState } from 'react';
import { SaleOrder, SaleOrderStatusCode } from 'models/SaleOrder';
import dayjs from 'dayjs';
import { formatPrice, getFilePath } from 'utils';
import { mapStatusToDisplay } from './SaleOrderDoneInfo';
import MemberRankBadge from '../Badge/MemberRankBadge';
import ProductItem from 'components/PurchaseOrder/ProductItem';
import { useProductImagesMap } from 'hooks/useProductImagesMap';
import { InputCommon } from '../Input/InputCommon';
import { PencilSimple, FloppyDisk, X } from '@phosphor-icons/react';
import { useUpdateSaleOrderMutation, useGetSaleOrderInvoiceExistenceQuery } from 'services/sale.service';
import { ButtonCommon, showAlertNotification } from 'components/common';
import { InfoFieldProps } from 'types/common';

const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  isHighlight = false,
  isDelayed,
  isValueBold,
  className,
  editable = false,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  fieldName,
}) => {
  const [inputValue, setInputValue] = useState(value as string);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSave?.(inputValue);
    } else if (e.key === 'Escape') {
      onCancel?.();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Update input value when the value prop changes
  React.useEffect(() => {
    if (!isEditing) {
      setInputValue(value as string);
    }
  }, [value, isEditing]);

  return (
    <div className={className}>
      <div className="flex items-center justify-start font-['Inter']">
        <div className={'text-gray-500 mb-1 text-sm'}>{label}</div>
        {editable && !isEditing && (
          <button
            className='ml-2 text-blue-500  mb-1 hover:text-blue-700'
            onClick={onEdit}
            aria-label={`Edit ${fieldName}`}
          >
            <PencilSimple size={16} weight='bold' />
          </button>
        )}
      </div>
      {isEditing ? (
        <div className='flex items-center gap-2'>
          <InputCommon
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoFocus
            hardSize='md2'
            className='flex-1'
            placeholder={`Nhập ${label.toLowerCase()}`}
          />
          <ButtonCommon
            className='!min-w-[38px] !h-[38px] !p-0 rounded-md'
            onClick={() => onSave?.(inputValue)}
            aria-label={`Save ${fieldName}`}
            color='success'
          >
            <FloppyDisk size={16} />
          </ButtonCommon>
          <ButtonCommon
            className='!min-w-[38px] !h-[38px] !p-0 rounded-md'
            onClick={onCancel}
            aria-label={`Cancel ${fieldName} edit`}
            color='dangerSoft'
          >
            <X size={16} />
          </ButtonCommon>
        </div>
      ) : (
        <div
          className={` font-medium ${
            isHighlight
              ? 'text-blue-500 !font-bold '
              : isDelayed
                ? 'text-red-500 !font-bold '
                : isValueBold
                  ? 'font-bold'
                  : ''
          }`}
        >
          {value || '-'}
        </div>
      )}
    </div>
  );
};

interface SaleOrderDetailInfoProps {
  saleOrder: SaleOrder;
}

const SaleOrderDetailInfo: React.FC<SaleOrderDetailInfoProps> = ({ saleOrder }) => {
  const [updateSaleOrder] = useUpdateSaleOrderMutation();
  const [editingField, setEditingField] = useState<string | null>(null);
  const { data: invoiceExistence } = useGetSaleOrderInvoiceExistenceQuery(saleOrder?.id || '', {
    skip: !saleOrder?.id,
  });

  const formattedDate = saleOrder?.createdAt ? dayjs(saleOrder?.createdAt).format('HH:mm Ngày DD/MM/YYYY') : '-';

  const statusDisplay = mapStatusToDisplay(saleOrder?.status?.code as SaleOrderStatusCode);
  const memberRank = 'Member Ship';
  const memberPoints = Number(saleOrder?.customer?.points) || 0;
  const productVersionIds = saleOrder?.saleOrderItems
    ? (saleOrder.saleOrderItems.map((item) => item.productVersionId).filter(Boolean) as string[])
    : [];
  const { imagesMap: productImagesMap } = useProductImagesMap(productVersionIds);

  const getImageUrl = (productVersionId: string) => {
    const imagePaths = productImagesMap?.[productVersionId];
    return imagePaths && imagePaths.length > 0 ? getFilePath() + '/' + imagePaths[0] : undefined;
  };
  // Handler to start editing a field
  const handleEdit = (fieldName: string) => {
    setEditingField(fieldName);
  };

  // Handler to cancel editing
  const handleCancel = () => {
    setEditingField(null);
  };

  // Handler to save the updated field
  const handleSave = async (fieldName: string, value: string) => {
    if (!saleOrder?.id) return;

    try {
      // Create the update payload with just the changed field
      const updateData: {
        id: string;
        [key: string]: string;
      } = {
        id: saleOrder.id,
      };

      switch (fieldName) {
        case 'customerName':
          updateData.customerName = value;
          break;
        case 'customerEmail':
          updateData.customerEmail = value;
          break;
        case 'customerPhone':
          updateData.customerPhone = value;
          break;
        case 'customerAddress':
          updateData.customerAddress = value;
          break;
        case 'description':
          updateData.description = value;
          break;
        default:
          break;
      }

      // Call the API to update the sale order
      await updateSaleOrder(updateData).unwrap();

      setEditingField(null);

      showAlertNotification({
        type: 'success',
        title: 'Cập nhật thành công',
        message: 'Thông tin đơn hàng đã được cập nhật',
      });
    } catch (error) {
      console.error('Failed to update sale order:', error);
      showAlertNotification({
        type: 'error',
        title: 'Cập nhật thất bại',
        message: 'Không thể cập nhật thông tin đơn hàng. Vui lòng thử lại sau.',
      });
    }
  };

  // Check if the order is editable (not canceled)
  const isOrderEditable = saleOrder?.status?.code !== SaleOrderStatusCode.CANCELLED;

  return (
    <div className='h-full p-6 pb-3 bg-white rounded-lg overflow-y-auto'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-[20px] leading-6 font-bold m-0'>THÔNG TIN ĐƠN HÀNG</h2>
        {invoiceExistence?.data?.exists && (
          <span className='text-[13px] font-semibold text-[var(--sematicSuccessColor500)] bg-[var(--sematicSuccessColor32)] px-3 py-1 rounded-full'>
            Đơn hàng này đã có hóa đơn điện tử
          </span>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4 p-3'>
        <InfoField label='Mã đơn hàng' value={saleOrder?.code} className='col-span-1' />

        <InfoField
          label='Trạng thái'
          value={
            <span className={`${statusDisplay.color} text-white px-4 py-1 rounded-full text-sm`}>
              {statusDisplay.text}
            </span>
          }
          className='col-span-1'
        />

        <InfoField label='Thời gian' value={formattedDate} className='col-span-1' />

        <InfoField
          label='Mã công nợ'
          value={saleOrder?.Debt?.[0]?.code || '---'}
          isHighlight={saleOrder?.status?.code === SaleOrderStatusCode.DEBT}
          className='col-span-1'
        />

        <InfoField
          label='Khách hàng'
          value={saleOrder?.customerName}
          className='col-span-1'
          editable={isOrderEditable}
          isEditing={editingField === 'customerName'}
          onEdit={() => handleEdit('customerName')}
          onSave={(value) => handleSave('customerName', value)}
          onCancel={handleCancel}
          fieldName='customerName'
        />

        <InfoField
          label='Mã khách hàng'
          value={saleOrder?.customer?.baseUser?.code}
          isHighlight={true}
          className='col-span-1'
        />

        <InfoField
          label='Số điện thoại'
          value={saleOrder?.customerPhone}
          className='col-span-1'
          isHighlight={true}
          editable={isOrderEditable}
          isEditing={editingField === 'customerPhone'}
          onEdit={() => handleEdit('customerPhone')}
          onSave={(value) => handleSave('customerPhone', value)}
          onCancel={handleCancel}
          fieldName='customerPhone'
        />

        <InfoField
          label='Email'
          value={saleOrder?.customerEmail}
          isHighlight={true}
          className='col-span-1'
          editable={isOrderEditable}
          isEditing={editingField === 'customerEmail'}
          onEdit={() => handleEdit('customerEmail')}
          onSave={(value) => handleSave('customerEmail', value)}
          onCancel={handleCancel}
          fieldName='customerEmail'
        />

        {memberPoints > 0 && (
          <div className='xl:col-span-4 lg:col-span-3 md:col-span-2 flex items-center gap-3 mt-2'>
            <div className='flex items-center gap-6'>
              <span className='text-[var(--textBlack56)]'>Điểm tích lũy:</span>{' '}
              <span className=''>{formatPrice(memberPoints || 0)} VNĐ</span>
              <MemberRankBadge name={memberRank} color='#FF823E' />
            </div>
          </div>
        )}

        <InfoField
          label='Địa chỉ'
          value={saleOrder?.customerAddress}
          className='xl:col-span-4 lg:col-span-3 md:col-span-2'
          editable={isOrderEditable}
          isEditing={editingField === 'customerAddress'}
          onEdit={() => handleEdit('customerAddress')}
          onSave={(value) => handleSave('customerAddress', value)}
          onCancel={handleCancel}
          fieldName='customerAddress'
        />

        {/* {saleOrder?.description && ( */}
        <div className='xl:col-span-4 lg:col-span-3 md:col-span-2 mt-2'>
          <InfoField
            label='Mô tả'
            editable={isOrderEditable}
            isEditing={editingField === 'description'}
            onEdit={() => handleEdit('description')}
            onSave={(value) => handleSave('description', value)}
            onCancel={handleCancel}
            fieldName='description'
            value={saleOrder?.description || ''}
            // value={<div className='border rounded-lg p-4 text-gray-700 min-h-[100px]'>{saleOrder?.description || ''}</div>}
            className='xl:col-span-4 lg:col-span-3 md:col-span-2'
          />
        </div>
        {/* )} */}
      </div>

      <div className='text-[20px] leading-6 font-bold mt-4 mb-2'>DANH SÁCH SẢN PHẨM</div>
      <div className='flex flex-col gap-2'>
        {saleOrder?.saleOrderItems?.map((item) => {
          // const category = item?.saleDimensions?.split('|').filter((item) => item !== '');
          // const dimension = item?.saleItemDimensions
          //   ?.map((itemSaleDemensions) => {
          //     const unit = itemSaleDemensions.dimensions?.split('|').filter((item) => item !== '');
          //     const value = unit.map((u, i) => `${u} ${category?.[i] || ''}`); // Ghép từng cặp
          //     return value.join(' x ');
          //   })
          //   .filter((dim) => dim !== '');

          const saleDimensions = item?.saleDimensions?.split('|').filter((item) => item !== '');
          const dimension = item?.saleItemDimensions
            ?.map((itemSaleDimensions) => {
              const unit = itemSaleDimensions.dimensions?.split('|');
              const value = (saleDimensions || []).map(
                (saleDimension, i) => `${unit[i] || '1'} ${saleDimension || ''}`,
              ); // Ghép từng cặp
              return value.join(' x ');
            })
            .filter((dim) => dim !== '');

          return (
            <ProductItem
              key={item.id}
              id={item.id}
              name={item.name || ''}
              unitPrice={item.salePrice}
              quantity={item.quantity}
              imageUrl={getImageUrl(item.productVersionId)}
              currencyUnit={'VNĐ'}
              unit={item.unit || ''}
              attribute={item.attributeName || ''}
              atrtributePrice={item.attributePrice || 0}
              dimension={dimension}
              isPOS
              isQuickView={true}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SaleOrderDetailInfo;
